import { useState, useEffect, useId } from 'react';
import {
  rankWith,
  ControlProps,
  isControl,
} from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { MenuItem, FormControl, InputLabel, Select, FormHelperText, Tooltip } from '@mui/material';
import { enumService, EnumValueDto } from '@/services/enumService';
import { EnumValueHelpText } from '@/features/help/HelpReadMore';

/**
 * Tester function to determine if this renderer should be used
 */
export const enumRendererTester = rankWith(
  20, // Very high priority to override Material-UI renderers (which typically use rank 1-2)
  (uischema, schema) => {
    if (!isControl(uischema) || !schema) {
      return false;
    }
    
    // IMPORTANT: Only match dynamic enums (with enumSource), NOT static enums (with enum array)
    // Static enums should use Material-UI's default enum renderer which handles schema.enum automatically
    
    // Check if schema has enum array (static enum) - if so, DON'T match
    const hasStaticEnum = !!(schema as any).enum && Array.isArray((schema as any).enum);
    if (hasStaticEnum) {
      // This is a static enum - let Material-UI handle it
      return false;
    }
    
    // Check if the schema has enumSource (for dynamic enums)
    // JSON Forms resolves schemas at the field path, so enumSource should be directly on the schema
    const hasEnumSource = !!(schema as any).enumSource;
    
    // Also check uischema options (fallback for cases where schema resolution might not work)
    const hasEnumSourceInOptions = !!(uischema.options as any)?.enumSource;
    
    return hasEnumSource || hasEnumSourceInOptions;
  }
);

/**
 * Enum renderer component for dynamic enums
 */
export const EnumRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, description, required, errors, schema, uischema } = props;
  // The entity the form edits: the form-schema generator titles a form's root schema with the entity's name.
  const formEntity = useJsonForms().core?.schema?.title;
  const isUnresolved = description?.endsWith('.description') && !description.includes(' ');
  const hasTooltip = description && description !== label && !isUnresolved;

  /**
   * MUI renders <Select> as a `div[role="combobox"]`, NOT an <input>, so a bare <InputLabel> next to
   * it labels nothing: there is no `for`/`id` pair to bind them and MUI only emits `aria-labelledby`
   * when it is given a `labelId`. Without this, every enum field in every generated form was a
   * REQUIRED control with no accessible name — a screen reader announced an unnamed combobox, and
   * clicking the label did not focus it (unlike every other field on the form).
   *
   * Bind them explicitly. `useId()` gives a stable, collision-free id per mounted control, which
   * matters because the same renderer is mounted once per enum field on a form.
   */
  const labelId = useId();
  const selectId = `${labelId}-select`;
  const [enumValues, setEnumValues] = useState<EnumValueDto[]>([]);
  const [showNumericCode, setShowNumericCode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get enumSource from schema or uischema options (CLI adds it to both)
  const enumSource = ((schema as any).enumSource || (uischema.options as any)?.enumSource) as string;

  // Optional per-control narrowing: a gate renderer that delegates here can exclude enum values that
  // are invalid in its context (e.g. HIERARCHY_CHILDREN on an ACCOUNT row axis) without a new enum type.
  const excludedValues = (uischema.options as any)?.excludeEnumValues as string[] | undefined;

  useEffect(() => {
    if (!enumSource) {
      setLoading(false);
      return;
    }

    // Load enum values and metadata
    const loadEnumValues = async () => {
      try {
        setLoading(true);
        setError(null);
        const [values, metadata] = await Promise.all([
          enumService.getEnumValues(enumSource),
          enumService.getEnumTypeMetadata(enumSource),
        ]);

        // Ensure values is an array
        if (!Array.isArray(values)) {
          setError(`Invalid response format for enum ${enumSource}`);
          setEnumValues([]);
          return;
        }

        // Sort by ordinal
        const sorted = values.sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
        setEnumValues(sorted);
        setShowNumericCode(metadata?.showNumericCode ?? false);
      } catch (err: any) {
        setError(err.message || `Failed to load enum values for ${enumSource}`);
        setEnumValues([]);
      } finally {
        setLoading(false);
      }
    };

    loadEnumValues();
  }, [enumSource]);

  const handleValueChange = (event: any) => {
    handleChange(path, event.target.value as string);
  };

  if (loading) {
    return (
      <FormControl fullWidth required={required} error={!!errors}>
        {hasTooltip ? (
          <Tooltip title={description} placement="top" arrow enterDelay={400}>
            <InputLabel id={labelId}>{label}</InputLabel>
          </Tooltip>
        ) : (
          <InputLabel id={labelId}>{label}</InputLabel>
        )}
        <Select labelId={labelId} id={selectId} value="" label={label} disabled>
          <MenuItem value="">Loading...</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (error) {
    // Clean up error message - remove HTML tags if present
    const cleanError = error.replace(/<[^>]*>/g, '').substring(0, 200);
    
    return (
      <FormControl fullWidth required={required} error={true}>
        {hasTooltip ? (
          <Tooltip title={description} placement="top" arrow enterDelay={400}>
            <InputLabel id={labelId}>{label}</InputLabel>
          </Tooltip>
        ) : (
          <InputLabel id={labelId}>{label}</InputLabel>
        )}
        <Select labelId={labelId} id={selectId} value={data || ''} label={label} disabled>
          <MenuItem value="">Error loading enum values</MenuItem>
        </Select>
        <FormHelperText>{cleanError}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth required={required} error={!!errors}>
      {hasTooltip ? (
        <Tooltip title={description} placement="top" arrow enterDelay={400}>
          <InputLabel id={labelId}>{label}</InputLabel>
        </Tooltip>
      ) : (
        <InputLabel id={labelId}>{label}</InputLabel>
      )}
      <Select
        labelId={labelId}
        id={selectId}
        value={data || ''}
        onChange={handleValueChange}
        label={label}
        displayEmpty={!required}
      >
        {!required && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {enumValues
          .filter((enumValue) => !excludedValues?.includes(enumValue.code))
          .map((enumValue) => (
            <MenuItem key={enumValue.code} value={enumValue.code}>
              {showNumericCode ? `${enumValue.ordinal} - ${enumValue.displayName}` : enumValue.displayName}
            </MenuItem>
          ))}
      </Select>
      {errors && <FormHelperText>{errors}</FormHelperText>}
      <EnumValueHelpText entity={formEntity} enumType={enumSource} value={data} />
    </FormControl>
  );
};

/**
 * Connected enum renderer with JSON Forms props
 */
export const EnumRenderer = withJsonFormsControlProps(EnumRendererComponent);

/**
 * Renderer entry for JSON Forms
 */
export const enumRendererEntry = {
  tester: enumRendererTester,
  renderer: EnumRenderer,
};

